import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
// Import styles
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { Routes, Route, Navigate } from "react-router-dom";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import { PageLayout } from "./components/layout/PageLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import HomePage from "./pages/HomePage";
import ObligoException from "./pages/obligoException";
import PermissionsPage from "./pages/Permissions";
import CheckInvoicesPage from "./pages/CheckInvoices";
import PurchaseInvoicePage from "./pages/purchaseInvoices/purchaseInvoicePage";
import TimeLinePage from "./pages/TimeLine";
import ViewInvoice from "./pages/purchaseInvoices/viewInvoice/viewInvoice";
import ViewAttachments from "./pages/purchaseInvoices/viewAttachments/viewAttachments";
import InvoiceRedirect from "./components/purchaseInvoices/redirect/InvoiceRedirect";
import EldanPrinterPage from "./pages/EldanPrinter";
import ChemoRecordsPage from "./pages/ChemoRecords";
import QrCodeDeviceMappingPage from "./pages/QrCodeDeviceMapping";
import AgentComplaintsPage from "./pages/AgentsComplain/AgentComplaints";
import AgentComplaintForm from "./pages/AgentsComplain/AgentComplaintForm";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import CheckedInvoicesTable from "./components/CheckedInvoices/CheckedInvoicesTable";
import InvoicesAndFioesUpload from "./components/UploadInvoicesAI/InvoicesAndFioesUpload";

import PriceListUpload from "./components/PriceListUpdate/PriceListUpload";
import ApproveLinesPage from "./pages/ApproveLines";
import TradeDelayPage from "./pages/TradeDelay";
import SalesPromotionsPage from "./pages/SalesPromotions";
import { UserProvider } from "./contexts/UserContext";
import { AgentProvider } from "./contexts/AgentContext";
import { BranchesProvider } from "./contexts/BranchesContext";
import { TransactionsTypeProvider } from "./contexts/TransactionsTypeContext";
import { ToastProvider } from "./contexts/ToastContext";
import { PermissionsProvider } from "./contexts/PermissionsContext";
import usePostLoginRedirect from "./hooks/usePostLoginRedirect";

const theme = createTheme({
  direction: "rtl",
});

const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});

// Create rtl theme
const rtlTheme = createTheme({
  direction: "rtl",
  typography: {
    fontFamily: "Roboto, Arial, sans-serif",
  },
});

const appStyle = {
  backgroundColor: "#f8f8f8",
  minHeight: "100vh",
  width: "100%",
};

const AppContent = () => {
  usePostLoginRedirect();

  return (
    <div className="App" dir="rtl" style={appStyle}>
      <PageLayout>
        <Routes>
          <Route path="/" element={<HomePage />} />

          <Route path="/i-v/:invoiceId" element={<InvoiceRedirect />} />

          <Route path="/i-r/:fileName" element={<ViewInvoice />} />

          <Route path="/v-a/:invoiceId" element={<ViewAttachments />} />

          <Route
            path="/Permissions"
            element={
              <ProtectedRoute requiredPermission="PermissionsPage">
                <PermissionsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/SalesPromotions/:orderId?"
            element={
              <ProtectedRoute requiredPermission="SalesPromotionsPage">
                <SalesPromotionsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/PurchaseInvoice"
            element={
              <ProtectedRoute requiredPermission="PurchaseInvoicePage">
                <PurchaseInvoicePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ChemoRecords"
            element={
              <ProtectedRoute requiredPermission="ChemoRecordsPage">
                <ChemoRecordsPage />
              </ProtectedRoute>
            }
          />

          {/* CheckInvoices routes with proper nesting */}
          <Route
            path="/CheckInvoices"
            element={
              <ProtectedRoute requiredPermission="CheckInvoicesPage">
                <CheckInvoicesPage />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="checked" replace />} />
            <Route path="checked" element={<CheckedInvoicesTable />} />
            <Route path="upload" element={<InvoicesAndFioesUpload />} />
            <Route path="pricelist" element={<PriceListUpload />} />
          </Route>

          <Route
            path="/ApproveLines"
            element={
              <ProtectedRoute requiredPermission="AproveLinesPage">
                <ApproveLinesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/TradeDelay"
            element={
              <ProtectedRoute requiredPermission="TradeDelayPage">
                <TradeDelayPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/ObligoException"
            element={
              <ProtectedRoute requiredPermission="ObligoExceptionPage">
                <ObligoException />
              </ProtectedRoute>
            }
          >
            <Route
              path=":custId"
              element={
                <ProtectedRoute requiredPermission="ObligoExceptionPage">
                  <ObligoException />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route
            path="/TimeLine"
            element={
              <ProtectedRoute requiredPermission="TimeLinePage">
                <TimeLinePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/Eldanprinter"
            element={
              <ProtectedRoute requiredPermission="EldanPrinterPage">
                <EldanPrinterPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/QrCodeDeviceMapping"
            element={
              <ProtectedRoute requiredPermission="QrCodeDeviceMappingPage">
                <QrCodeDeviceMappingPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/agent-complaints"
            element={
              <ProtectedRoute requiredPermission="AgentComplaintsPage">
                <AgentComplaintsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/agent-complaints/new"
            element={
              <ProtectedRoute requiredPermission="AgentComplaintsPage">
                <AgentComplaintForm />
              </ProtectedRoute>
            }
          />

        </Routes>
      </PageLayout>
    </div>
  );
};

const App = ({ instance }) => {
  const queryClient = new QueryClient();

  return (
    <MsalProvider instance={instance}>
      <UserProvider>
        <AgentProvider>
        <BranchesProvider>
          <TransactionsTypeProvider>
            <ToastProvider>
              <PermissionsProvider>
                <CacheProvider value={cacheRtl}>
                  <ThemeProvider theme={theme}>
                    <Worker workerUrl="/pdf.worker.js">
                      <AppContent />
                    </Worker>
                  </ThemeProvider>
                </CacheProvider>
              </PermissionsProvider>
            </ToastProvider>
          </TransactionsTypeProvider>
        </BranchesProvider>
        </AgentProvider>
      </UserProvider>
    </MsalProvider>
  );
};

export default App;
